import http from "@/confs/interceptors/http";
import type { CustomAxiosRequestConfig } from "@/confs/interceptors/utils/interface";
import type { PaginationFilter } from "@/interfaces/common/paginations";
import type { AxiosProgressEvent } from "axios";

export const httpService = {
    async get<T>(uri: string, options: CustomAxiosRequestConfig | {} = {}): Promise<T> {
        const response = await http.get<T>(uri, options);
        return response.data;
    },
    async post<T, D = unknown>(
        uri: string,
        data: D,
        options: CustomAxiosRequestConfig | {} = {}
    ): Promise<T> {
        const response = await http.post<T>(uri, data, options);
        return response.data;
    },
    async postForm<T>(uri: string, form: FormData): Promise<T> {
        const response = await http.post<T>(uri, form, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },
    async put<T, D = unknown>(
        uri: string,
        data: D,
        options: CustomAxiosRequestConfig | {} = {}
    ): Promise<T> {
        const response = await http.put<T>(uri, data, options);
        return response.data;
    },
    async patch<T, D = unknown>(
        uri: string,
        data: D,
        options: CustomAxiosRequestConfig | {} = {}
    ): Promise<T> {
        const response = await http.patch<T>(uri, data, options);
        return response.data;
    },
    async delete<T, D = unknown>(
        uri: string,
        data: D | {} = {},
        options: CustomAxiosRequestConfig | {} = {}
    ): Promise<T> {
        const optionsData = {
            ...{ method: 'delete', url: uri, data },
            options
        }
        const response = await http.request<T>(optionsData);
        return response.data;
    },
    buildQueryParams(filter: PaginationFilter) {
        const params = new URLSearchParams()

        if (filter.page !== undefined) params.append("page", String(filter.page))
        if (filter.limit !== undefined) params.append("limit", String(filter.limit))
        if (filter.sort) params.append("sort", filter.sort)
        if (filter.sort_direction) params.append("sort_direction", filter.sort_direction)
        if (filter.search) params.append("search", filter.search)
        if ((filter as any).phone) params.append("phone", String((filter as any).phone))
        if ((filter as any).phone_number) params.append("phone_number", String((filter as any).phone_number))
        if ((filter as any).month) params.append("month", String((filter as any).month))

        const columns = filter.columns?.join(',');

        if (columns) {
            params.append("columns", columns)
        }

        // Filters dinâmicos: cada chave-valor vira query param
        if (filter.filters) {
            Object.entries(filter.filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value)
                }
            })
        }

        return params.toString()
    },
    async uploadTempFile(file: File,
        options?: {
            onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
            signal?: any
            skipLoading?: boolean
        }
    ): Promise<{ data: { path: string } }> {

        const formData = new FormData()
        formData.append('file', file);

        return http.post('/public/utils/file/tmp', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }, onUploadProgress: options?.onUploadProgress,
            signal: options?.signal,
            skipLoading: options?.skipLoading
        } as CustomAxiosRequestConfig)
    },
    async downloadFile(uri: string, fileName: string) {
        const res = await http.get(uri, {
            responseType: 'blob',
            fullResponse: true
        } as CustomAxiosRequestConfig);

        // Cria um link temporário para download
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
    async downloadFilePost(uri: string, data: any, fileName: string = "") {
        const res = await http.post(uri, data, {
            responseType: 'blob',
            fullResponse: true
        } as CustomAxiosRequestConfig);


        if (!fileName) { // Caso não informado o nome do arquivo, obtém do header
            const contentDisposition = res.headers?.['content-disposition'];
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (fileNameMatch?.[1]) {
                    fileName = fileNameMatch[1];
                }
            }
        }

        // Cria um link temporário para download
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }, async loadFile(uri: string): Promise<string> {
        const res = await http.get(uri, {
            responseType: 'blob',
            fullResponse: true
        } as CustomAxiosRequestConfig);

        return window.URL.createObjectURL(res.data);
    }, getUrl(uri: string) {
        return `${import.meta.env.VITE_APP_URL}${uri}`
    }
}